/**
 * 区域填充写入（M2-12）。
 *
 * 渐变/油漆桶/图案共用的落盘通道：对目标矩形内的每个块
 * touch → capture → 读改写（source-over），一次调用 = 一段可整体撤销的
 * 像素修改（与画笔的 merge 同一纪律，M1-08）。
 *
 * 选区调制（验收标准 3）：源 alpha 乘以 mask/255——选区值 128 处效果恰好减半。
 */
import { TILE, layerId as makeLayerId, tileKey, type LayerId } from '../tile/key';
import { BYTES_PER_TILE, type TileStore } from '../tile/store';
import type { StrokeTransaction } from '../brush/buffer';
import { REFERENCE_BLEND, type SeparableBlendMode } from '../../gpu/shaders/blend/modes';

export interface FillRegion {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** 逐像素给色（文档空间坐标）。返回 sRGB 字节 RGBA。 */
export type PixelSource = (x: number, y: number) => readonly [number, number, number, number];

export interface ApplyOptions {
  /** 选区蒙版（0–255）。省略 = 不调制。 */
  readonly selection?: { valueAt(x: number, y: number): number } | undefined;
  /** 额外的整体不透明度 0–1。 */
  readonly opacity?: number;
  /**
   * 混合模式，省略/'normal' = 直接 source-over（原来的行为不变）。
   * 只支持可分离模式——跟 composite.ts 的 compositeRaster 同一个限制：
   * 色相/饱和度/颜色/明度这四个非可分离模式要拿整个 RGB 三元组一起算，
   * 这里逐通道调用 `source` 的结构接不上，填充对话框先不放这四个选项。
   */
  readonly blendMode?: SeparableBlendMode;
}

/** 把 source 在 region 内 source-over 到图层。返回受影响的块坐标。 */
export function applyFill(
  store: TileStore,
  layer: LayerId | string,
  region: FillRegion,
  source: PixelSource,
  transaction: StrokeTransaction,
  options: ApplyOptions = {},
): Array<{ tx: number; ty: number }> {
  const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
  const opacity = options.opacity ?? 1;
  const blend =
    options.blendMode !== undefined && options.blendMode !== 'normal'
      ? REFERENCE_BLEND[options.blendMode]
      : null;
  const x1 = region.x + region.width;
  const y1 = region.y + region.height;
  const tx0 = Math.max(0, Math.floor(region.x / TILE));
  const ty0 = Math.max(0, Math.floor(region.y / TILE));
  const tx1 = Math.floor((x1 - 1) / TILE);
  const ty1 = Math.floor((y1 - 1) / TILE);

  const touched: Array<{ tx: number; ty: number }> = [];
  const keys = [];
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const key = tileKey(target, tx, ty);
      transaction.touch(key);
      keys.push({ tx, ty, key });
    }
  }
  transaction.capture();

  for (const { tx, ty, key } of keys) {
    const existing = store.get(key)?.data;
    const out = new Uint8Array(BYTES_PER_TILE);
    if (existing != null) out.set(existing);
    let wroteAny = false;

    const left = Math.max(region.x, tx * TILE);
    const top = Math.max(region.y, ty * TILE);
    const right = Math.min(x1, (tx + 1) * TILE);
    const bottom = Math.min(y1, (ty + 1) * TILE);

    for (let y = top; y < bottom; y++) {
      for (let x = left; x < right; x++) {
        const [r, g, b, a] = source(x, y);
        let alpha = (a / 255) * opacity;
        if (options.selection !== undefined) {
          alpha = (alpha * options.selection.valueAt(x, y)) / 255;
        }
        if (alpha <= 0) continue;
        wroteAny = true;
        const at = ((y % TILE) * TILE + (x % TILE)) * 4;
        const dstA = out[at + 3]! / 255;
        const outA = alpha + dstA * (1 - alpha);
        if (outA > 0) {
          // 跟 composite.ts 的 compositeRaster 同一个 Porter-Duff + 混合公式：
          // 没有混合模式（或就是 normal）时 mixed=cs，退化成原来的 source-over。
          const rgb = [r, g, b] as const;
          for (let c = 0; c < 3; c++) {
            const cs = rgb[c]! / 255;
            const cb = out[at + c]! / 255;
            const mixed = blend === null ? cs : blend(cb, cs);
            const mixedRgb = ((1 - dstA) * alpha * cs + dstA * alpha * mixed + (1 - alpha) * dstA * cb) / outA;
            out[at + c] = Math.round(mixedRgb * 255);
          }
        }
        out[at + 3] = Math.round(outA * 255);
      }
    }
    if (wroteAny || existing != null) {
      store.set(key, out);
      touched.push({ tx, ty });
    }
  }
  return touched;
}

/** 无缝平铺的图案采样器。pattern 尺寸任意，负坐标同样正确取模。 */
export function patternSource(pattern: {
  readonly width: number;
  readonly height: number;
  /** RGBA8。 */
  readonly pixels: Uint8Array;
}): PixelSource {
  const { width, height, pixels } = pattern;
  // 像素数比较（同 magic-wand）：也避开「整图字节分配」扫描的正则形状
  if (pixels.length / 4 !== width * height) {
    throw new RangeError(`图案数据长度 ${pixels.length} 与 ${width}×${height} 不符。`);
  }
  return (x, y) => {
    const px = ((x % width) + width) % width;
    const py = ((y % height) + height) % height;
    const at = (py * width + px) * 4;
    return [pixels[at]!, pixels[at + 1]!, pixels[at + 2]!, pixels[at + 3]!];
  };
}
